import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import { VariablePill } from '../../../App/InspectorDrawer/ConfigurationPanel/input-panels/helpers/inputs/VariablePillExtension';
import { VariableSuggestion } from '../../../App/InspectorDrawer/ConfigurationPanel/input-panels/helpers/inputs/VariableSuggestion';
import { convertMarkdownVariablesToHTML } from '../../../App/InspectorDrawer/ConfigurationPanel/input-panels/helpers/inputs/convertMarkdownToPills';
import { Heading1, Heading2, Heading3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import 'tippy.js/dist/tippy.css';
import '../../../App/InspectorDrawer/ConfigurationPanel/input-panels/helpers/inputs/variable-suggestion.css';
import { convertEmailBuilderStylesToCSS } from '../Text/convertEmailBuilderStyles';

type InlineHeadingEditorProps = {
  initialContent: string;
  onSave: (markdown: string, level: 'h1' | 'h2' | 'h3') => void;
  blockId: string;
  style?: any; // Email-builder-js style format
  level: 'h1' | 'h2' | 'h3';
};

export default function InlineHeadingEditor({
  initialContent,
  onSave,
  blockId,
  style,
  level,
}: InlineHeadingEditorProps) {
  const contentRef = useRef(initialContent);
  const levelRef = useRef(level);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // Convert email-builder-js style format to React CSS properties
  const cssStyle = convertEmailBuilderStylesToCSS(style);

  // Add heading-specific styles (fontSize based on level, default bold)
  const getFontSize = (headingLevel: 'h1' | 'h2' | 'h3') => {
    switch (headingLevel) {
      case 'h1':
        return '32px';
      case 'h2':
        return '24px';
      case 'h3':
        return '20px';
    }
  };

  const headingStyle = {
    ...cssStyle,
    fontSize: getFontSize(level),
    fontWeight: cssStyle.fontWeight || 'bold', // Default to bold if not specified
  };

  // Track active heading level
  const [activeLevel, setActiveLevel] = useState<'h1' | 'h2' | 'h3'>(level);

  const updateActiveLevel = (editor: any) => {
    if (editor.isActive('heading', { level: 1 })) {
      setActiveLevel('h1');
    } else if (editor.isActive('heading', { level: 2 })) {
      setActiveLevel('h2');
    } else if (editor.isActive('heading', { level: 3 })) {
      setActiveLevel('h3');
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3], // H1, H2, H3
        },
        codeBlock: false,
        code: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: 'Type your heading...',
      }),
      VariablePill,
      VariableSuggestion.configure({
        isEmail: true,
      }),
      Markdown.configure({
        html: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: convertMarkdownVariablesToHTML(initialContent),
    editable: true,
    autofocus: 'end',
    onUpdate: ({ editor }) => {
      const markdown = editor.storage.markdown?.getMarkdown() || editor.getText();
      contentRef.current = markdown;
      updateActiveLevel(editor);
    },
    onSelectionUpdate: ({ editor }) => {
      updateActiveLevel(editor);
    },
    onTransaction: ({ editor }) => {
      updateActiveLevel(editor);
    },
    onBlur: ({ editor }) => {
      const markdown = editor.storage.markdown?.getMarkdown() || editor.getText();
      onSave(markdown, levelRef.current);
    },
    editorProps: {
      attributes: {
        class: 'inline-tiptap-heading-editor',
      },
    },
  });

  // Set initial heading level
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const levelNumber = level === 'h1' ? 1 : level === 'h2' ? 2 : 3;
      editor.chain().setHeading({ level: levelNumber }).run();
    }
  }, [editor]);

  // Update content if initialContent changes
  useEffect(() => {
    if (editor && initialContent !== contentRef.current) {
      const htmlContent = convertMarkdownVariablesToHTML(initialContent);
      editor.commands.setContent(htmlContent);
      contentRef.current = initialContent;
    }
  }, [initialContent, editor]);

  if (!editor) {
    return null;
  }

  const setHeadingLevel = (newLevel: 'h1' | 'h2' | 'h3') => {
    const levelNumber = newLevel === 'h1' ? 1 : newLevel === 'h2' ? 2 : 3;
    editor.chain().focus().setHeading({ level: levelNumber }).run();
    levelRef.current = newLevel;
    setActiveLevel(newLevel);
  };

  return (
    <div
      style={{
        position: 'relative',
        ...headingStyle,
      }}
      ref={editorContainerRef}
    >
      <style>{`
        .inline-tiptap-heading-editor {
          outline: none;
          width: 100%;
        }
        .inline-tiptap-heading-editor h1,
        .inline-tiptap-heading-editor h2,
        .inline-tiptap-heading-editor h3,
        .inline-tiptap-heading-editor p {
          margin: 0;
        }
        .inline-tiptap-heading-editor a {
          color: hsl(var(--primary));
          text-decoration: underline;
        }
        .inline-tiptap-heading-editor strong {
          font-weight: bold;
        }
        .inline-tiptap-heading-editor em {
          font-style: italic;
        }
        .inline-tiptap-heading-editor u {
          text-decoration: underline;
        }
        .inline-tiptap-heading-editor s {
          text-decoration: line-through;
        }
        .inline-tiptap-heading-editor .is-editor-empty:first-child::before {
          color: #999;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        /* Variable pill styles */
        .inline-tiptap-heading-editor .variable-pill {
          color: white;
        }
        .inline-tiptap-heading-editor .variable-pill:hover .pill-delete {
          display: inline-block !important;
        }
        .inline-tiptap-heading-editor .variable-pill .pill-delete:hover {
          color: rgb(239, 68, 68);
        }
        /* Floating toolbar */
        .floating-heading-toolbar {
          position: absolute;
          top: -48px;
          left: 0;
          z-index: 50;
          animation: fadeIn 0.15s ease-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      {/* Floating toolbar positioned above editor */}
      <div ref={toolbarRef} className="floating-heading-toolbar">
        <ButtonGroup className="shadow-lg bg-background border border-border rounded-md">
          <Button
            size="sm"
            variant={activeLevel === 'h1' ? 'default' : 'ghost'}
            onClick={() => setHeadingLevel('h1')}
          >
            <Heading1 className="h-4 w-4 text-foreground" />
          </Button>

          <Button
            size="sm"
            variant={activeLevel === 'h2' ? 'default' : 'ghost'}
            onClick={() => setHeadingLevel('h2')}
          >
            <Heading2 className="h-4 w-4 text-foreground" />
          </Button>

          <Button
            size="sm"
            variant={activeLevel === 'h3' ? 'default' : 'ghost'}
            onClick={() => setHeadingLevel('h3')}
          >
            <Heading3 className="h-4 w-4 text-foreground" />
          </Button>
        </ButtonGroup>
      </div>

      {/* Editor content */}
      <EditorContent editor={editor} />
    </div>
  );
}
